"use client"
import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Box, Typography } from "@mui/material"

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledCarTypesGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`

const StyledCarTypeCard = styled(Box)<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 20px;
  background-color: ${sharedColors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:focus-visible {
    border: 1px dashed white;
    outline: 2px solid black;
    outline-offset: 2px;
  }
`

const StyledCarIcon = styled.img<{ selected: boolean }>`
  width: 48px;
  height: 24px;
  object-fit: contain;
`

const StyledCarTypeLabel = styled(Typography)<{ selected: boolean }>`
  font-size: 14px;
  line-height: 130%;
  text-align: center;
  font-weight: 400;
  text-transform: capitalize;
  color: ${(props) =>
    props.selected ? sharedColors.black : sharedColors.black};
`

interface CarTypeSectionProps {
  selectedCarTypes: string[]
  toggleCarType: (carTypeId: string) => void
  carTypes: Array<{
    id: string
    label: string
    icon: string
    imageNormal?: string
    imageOn?: string
  }>
}

function CarTypeSection({
  selectedCarTypes,
  toggleCarType,
  carTypes,
}: CarTypeSectionProps) {
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const { key } = e
    let newIndex: number | null = null

    switch (key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault()
        newIndex = (index + 1) % carTypes.length
        break
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault()
        newIndex = (index - 1 + carTypes.length) % carTypes.length
        break
      case " ":
      case "Enter":
        e.preventDefault()
        toggleCarType(carTypes[index].id)
        return
    }

    if (newIndex !== null) {
      const nextElement = document.querySelector(
        `[data-cartype-index="${newIndex}"]`,
      ) as HTMLElement
      nextElement?.focus()
    }
  }

  // Determine which item should be tabbable (first selected, or first item)
  const firstSelectedIndex = carTypes.findIndex((ct) =>
    selectedCarTypes.includes(ct.id),
  )
  const tabbableIndex = firstSelectedIndex >= 0 ? firstSelectedIndex : 0

  return (
    <StyledSection>
      <Typography variant="body2" fontWeight={600} id="cartype-group-label">
        סוג הרכב
      </Typography>
      <StyledCarTypesGrid
        role="radiogroup"
        aria-labelledby="cartype-group-label"
      >
        {carTypes.map((carType, index) => {
          const isSelected = selectedCarTypes.includes(carType.id)
          // Use Umbraco images if available, otherwise fall back to icon
          const imageSrc =
            carType.imageNormal && carType.imageOn
              ? isSelected
                ? carType.imageOn
                : carType.imageNormal
              : carType.icon

          return (
            <StyledCarTypeCard
              key={carType.id}
              selected={isSelected}
              onClick={() => toggleCarType(carType.id)}
              role="radio"
              aria-checked={isSelected}
              aria-label={carType.label}
              tabIndex={index === tabbableIndex ? 0 : -1}
              data-cartype-index={index}
              onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index)}
            >
              <StyledCarIcon
                src={imageSrc}
                alt=""
                aria-hidden="true"
                selected={isSelected}
              />
              <StyledCarTypeLabel selected={isSelected}>
                {carType.label}
              </StyledCarTypeLabel>
            </StyledCarTypeCard>
          )
        })}
      </StyledCarTypesGrid>
    </StyledSection>
  )
}

export default CarTypeSection
